import { Text } from "@fluentui/react-northstar";
import { Person } from "@microsoft/mgt-react";
import React from "react";
import {
  CustomFormAnswer,
  CustomFormTemplate,
  CustomFormTemplateBody,
  CustomFormTypesExcempt,
  MgtUser,
} from "../../domain/CustomForm";

import { useTranslation } from "react-i18next";
import checkboxImg from "../../../images/checkbox.png";
import uncheckboxImg from "../../../images/uncheckbox.png";
import { appState } from "../../AppState";
import { ConvertDateToLocale } from "../../shared/common/ConvertDate";

type CustomFormAnswersProps = {
  cardAnswerJSON?: CustomFormAnswer;
  cardTemplateJSON?: CustomFormTemplate;
  customForms?: {
    formName?: string;
    cardAnswerJSON: CustomFormAnswer;
    cardTemplateJSON: CustomFormTemplate;
  }[];
};

export const PrintCustomFormAnswers: React.FC<CustomFormAnswersProps> = ({
  cardAnswerJSON,
  cardTemplateJSON,
  customForms,
}) => {
  const currentState = appState();
  const { t } = useTranslation();
  const stringifyValue = (value: unknown): string => {
    if (value == null) {
      return "";
    }

    if (typeof value === "string") {
      return value;
    }

    if (
      typeof value === "number" ||
      typeof value === "boolean" ||
      typeof value === "bigint" ||
      typeof value === "symbol"
    ) {
      return value.toString();
    }

    try {
      return JSON.stringify(value);
    } catch {
      return "";
    }
  };

  const getPrintableFields = (
    templateBody: unknown
  ): CustomFormTemplateBody[] => {
    const result: CustomFormTemplateBody[] = [];

    const walk = (node: unknown) => {
      if (!node) {
        return;
      }

      if (Array.isArray(node)) {
        node.forEach(walk);
        return;
      }

      if (typeof node !== "object") {
        return;
      }

      const typedNode = node as {
        type?: string;
        id?: string;
        items?: unknown[];
        columns?: { items?: unknown[] }[];
      };

      if (typedNode.columns && Array.isArray(typedNode.columns)) {
        typedNode.columns.forEach((column) => walk(column?.items ?? []));
      }

      if (typedNode.items && Array.isArray(typedNode.items)) {
        walk(typedNode.items);
      }

      if (typedNode.type && typedNode.id) {
        result.push(typedNode as CustomFormTemplateBody);
      }
    };

    walk(templateBody);
    return result;
  };

  const parseEntityId = (answerValue: unknown): number | null => {
    if (answerValue == null) {
      return null;
    }

    if (typeof answerValue === "number" && Number.isFinite(answerValue)) {
      return answerValue;
    }

    const rawText = stringifyValue(answerValue).trim();
    if (!rawText) {
      return null;
    }

    try {
      const parsed = JSON.parse(rawText);
      if (typeof parsed === "number" && Number.isFinite(parsed)) {
        return parsed;
      }
      if (typeof parsed === "string") {
        const parsedStringId = Number.parseInt(parsed, 10);
        return Number.isNaN(parsedStringId) ? null : parsedStringId;
      }
    } catch {
      // Fallback to raw string parsing.
    }

    const directId = Number.parseInt(rawText, 10);
    return Number.isNaN(directId) ? null : directId;
  };

  const parseValue = (
    cardAnswerJSONValue: unknown,
    cardTemplateJSONValue: CustomFormTemplateBody
  ) => {
    const answerText = stringifyValue(cardAnswerJSONValue);

    try {
      switch (cardTemplateJSONValue.type) {
        case "Assignment":
          const assignmentId = parseEntityId(cardAnswerJSONValue);
          const assignment = currentState.usersInRole.find(
            (p) => p.Id === assignmentId
          );
          if (!assignment) {
            return <Text size="smaller">{answerText}</Text>;
          }
          return (
            <div className="flex items-center" key={assignment.AadObjectId}>
              <Person userId={assignment.AadObjectId} />
              <Text size="smaller" className="ml-1">
                {assignment.FullName}
              </Text>
            </div>
          );
        case "Collaborators":
          const userIds = answerText.split(",").filter(Boolean);
          return (
            <div className="flex items-center gap-2">
              {userIds.map((user: string) => {
                const platformuser = currentState.usersInRole.find(
                  (p) => p.Id === parseInt(user, 10)
                );
                if (!platformuser) {
                  return null;
                }
                return (
                  <div
                    className="flex items-center"
                    key={platformuser.AadObjectId}
                  >
                    <Person userId={platformuser.AadObjectId} />
                    <Text size="smaller" className="ml-1">
                      {platformuser.FullName}
                    </Text>
                  </div>
                );
              })}
            </div>
          );
        case "Input.Text":
        case "Input.Number":
          return <Text size="smaller">{answerText}</Text>;
        case "Input.Date":
        case "Resolution Date":
        case "Due Date":
          return (
            <Text size="smaller">
              {answerText
                ? new Date(answerText).toLocaleDateString()
                : ""}
            </Text>
          );
        case "Input.Time":
          return (
            <Text size="smaller">
              {answerText
                ? ConvertDateToLocale(answerText, {
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                    hour12: true, // optional: for 12-hour format
                  })
                : ""}
            </Text>
          );
        case "Input.Toggle":
          return (
            <div className="flex align-items mt-1">
              <Text size="smaller">
                {stringifyValue(cardAnswerJSONValue).toLowerCase() === "true"
                  ? "Yes"
                  : "No"}
              </Text>
            </div>
          );
        case "Input.ChoiceSet":
          const selectedChoices = answerText.split(",").filter(Boolean);
          return (
            <div className="flex align-items gap-2 my-1 flex-wrap">
              {cardTemplateJSONValue.choices.map((choice) => (
                <div className="flex align-items gap-1" key={choice.value}>
                  <img
                    src={
                      selectedChoices.includes(choice.value)
                        ? checkboxImg
                        : uncheckboxImg
                    }
                    alt="check box"
                    width="16px"
                    height="16px"
                  />
                  <Text size="smaller" className="mt-0.5">
                    {choice.title}
                  </Text>
                </div>
              ))}
            </div>
          );

        case "People Picker":
        case "Requester":
        case "Affected Users":
          const result =
            typeof cardAnswerJSONValue === "string"
              ? (JSON.parse(cardAnswerJSONValue) as MgtUser[])
              : ((cardAnswerJSONValue as MgtUser[]) ?? []);
          return (
            <div className="flex items-center gap-2">
              {result.map((user) => (
                <div className="flex items-center" key={user.AadObjectId}>
                  <Person userId={user.AadObjectId} />
                  <Text size="smaller" className="ml-1">
                    {user.FullName}
                  </Text>
                </div>
              ))}
            </div>
          );

        case "Status":
          const status = parseEntityId(cardAnswerJSONValue);
          return (
            <Text size="smaller">
              {currentState.ticketStatus.find((s) => s.Id === status)?.Value ??
                answerText}
            </Text>
          );

        case "Team":
          const team = parseEntityId(cardAnswerJSONValue);
          return (
            <Text size="smaller">
              {currentState.teams.find((t) => t.Id === team)?.FriendlyName ??
                answerText}
            </Text>
          );
        case "Ticket Type":
          const ticketType = parseEntityId(cardAnswerJSONValue);
          return (
            <Text size="smaller">
              {currentState.ticketTypes.find((t) => t.Id === ticketType)
                ?.Value ?? answerText}
            </Text>
          );
        case "Group":
          const group = parseEntityId(cardAnswerJSONValue);
          return (
            <Text size="smaller">
              {currentState.supportgroups.find((t) => t.Id === group)?.Name ??
                answerText}
            </Text>
          );
        case "Category":
          const category = parseEntityId(cardAnswerJSONValue);
          return (
            <Text size="smaller">
              {currentState.categories.find((t) => t.Id === category)?.Value ??
                answerText}
            </Text>
          );
        case "Priority":
          const priority = parseEntityId(cardAnswerJSONValue);
          return (
            <Text size="smaller">
              {currentState.priority.find((t) => t.Id === priority)?.Value ??
                answerText}
            </Text>
          );
        case "Tags":
          const tags = answerText.split(",").filter(Boolean);
          return (
            <>
              {tags.map((tag: string, index) => (
                <Text size="smaller" key={tag}>
                  {currentState.tags.find((t) => t.Id == parseInt(tag, 10))?.[
                    "Name"
                  ] ?? ""}{" "}
                  {index !== tags.length - 1 && ", "}
                </Text>
              ))}
            </>
          );

        default:
          return <Text size="smaller">{answerText}</Text>;
      }
    } catch {
      return <Text size="smaller">{answerText}</Text>;
    }
  };

  const formsToRender = (() => {
    if (customForms && customForms.length > 0) {
      return customForms;
    }

    if (cardTemplateJSON) {
      return [
        {
          formName: "",
          cardAnswerJSON: cardAnswerJSON ?? {},
          cardTemplateJSON,
        },
      ];
    }

    return [];
  })();

  if (formsToRender.length === 0) {
    return null;
  }

  return (
    <>
      <div className="mb-2">
        <Text size="small" weight="semibold">
          {t("ticket-details.edit-ticket-form.custom-form-answer.title")}
        </Text>
      </div>
      {formsToRender.map((form) => {
        const printableFields = getPrintableFields(form.cardTemplateJSON?.body ?? []);
        const getFieldIdentity = (field: CustomFormTemplateBody) =>
          "id" in field ? field.id : field.type;
        const formKey = `${form.formName ?? ""}-${printableFields
          .map((field) => getFieldIdentity(field))
          .join("-")}`;
        return (
          <div key={formKey} className={formsToRender[0] !== form ? "mt-3" : ""}>
            {form.formName ? (
              <div className="mb-1">
                <Text size="small" weight="semibold">
                  {form.formName}
                </Text>
              </div>
            ) : null}
            <div className="border border-b-0 div-table w-full">
              {printableFields?.map(
                (value) =>
                  !CustomFormTypesExcempt.includes(value["type"]) && (
                    <div className="flex w-full border-b" key={`${formKey}-${value["id"]}`}>
                      <div className="flex-grow w-1/4 p-1">
                        <Text size="smaller">
                          {(() => {
                            const label =
                              value["label"] ??
                              value["displayLabel"] ??
                              value["title"] ??
                              value["id"]?.replaceAll(".", " ") ??
                              value["type"]?.replaceAll(".", " ") ??
                              "";

                            return label;
                          })()}
                        </Text>
                      </div>
                      <div className="flex-grow w-1/4 p-1 border-l">
                        {parseValue(form.cardAnswerJSON[value["id"]] ?? "", value) ?? ""}
                      </div>
                    </div>
                  )
              )}
            </div>
          </div>
        );
      })}
    </>
  );
};
